/**
 * Palette derivation — ported from clario-frontend (and the CRM app's
 * apps/crm/src/utils/theme/palette.ts).
 *
 * Turns the single stored `railColor` anchor into a full 11-shade indigo ramp
 * (50–950) as "R G B" triplet strings, so overriding the `--indigo-*` CSS vars
 * (and `--primary`) re-themes every Tailwind `indigo-*` / `primary` / `ring`
 * token across the whole app at runtime.
 */

import { hexToRgb, blendHex, isDarkColor } from './sidebarTheme';

// ── Types ─────────────────────────────────────────────────────────────

export type PaletteShade =
  | '50' | '100' | '200' | '300' | '400'
  | '500'
  | '600' | '700' | '800' | '900' | '950';

/** Each value is an "R G B" triplet string (e.g. "99 102 241") for Tailwind alpha-value compatibility. */
export type PaletteMap = Record<PaletteShade, string>;

export const PALETTE_SHADES: PaletteShade[] = [
  '50', '100', '200', '300', '400',
  '500',
  '600', '700', '800', '900', '950',
];

// ── Color-space helpers ──────────────────────────────────────────────

/** Convert a hex color to "R G B" triplet string. Returns "0 0 0" if parsing fails. */
function hexToTriplet(hex: string): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return '0 0 0';
  return `${rgb.r} ${rgb.g} ${rgb.b}`;
}

/** Convert RGB (0-255) to HSL (h: 0-360, s: 0-1, l: 0-1). */
function rgbToHsl(r: number, g: number, b: number): { h: number; s: number; l: number } {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;

  if (max === min) return { h: 0, s: 0, l };

  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  if (max === rn) h = ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6;
  else if (max === gn) h = ((bn - rn) / d + 2) / 6;
  else h = ((rn - gn) / d + 4) / 6;

  return { h: h * 360, s, l };
}

/** Convert HSL (h: 0-360, s: 0-1, l: 0-1) to hex string. */
function hslToHex(h: number, s: number, l: number): string {
  const hue2rgb = (p: number, q: number, t: number) => {
    let tn = t;
    if (tn < 0) tn += 1;
    if (tn > 1) tn -= 1;
    if (tn < 1 / 6) return p + (q - p) * 6 * tn;
    if (tn < 1 / 2) return q;
    if (tn < 2 / 3) return p + (q - p) * (2 / 3 - tn) * 6;
    return p;
  };

  let r: number, g: number, b: number;
  if (s === 0) {
    r = g = b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h / 360 + 1 / 3);
    g = hue2rgb(p, q, h / 360);
    b = hue2rgb(p, q, h / 360 - 1 / 3);
  }

  const toHex = (c: number) =>
    Math.round(Math.max(0, Math.min(255, c * 255)))
      .toString(16)
      .padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
}

/**
 * Derive a vibrant mid-tone (shade 500) from any color.
 *
 * For dark colors: keeps the hue, boosts saturation to at least 55%,
 * and sets lightness to ~52% (typical Tailwind 500 shade lightness).
 * This preserves the color identity without producing muddy grays.
 */
function deriveVibrantMidTone(hex: string): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;

  const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);

  // If nearly achromatic (saturation < 10%), keep it as a gray tone
  // (e.g., slate, zinc) — boost lightness to ~48% for a mid-gray.
  if (hsl.s < 0.1) {
    return hslToHex(hsl.h, hsl.s, 0.48);
  }

  // Chromatic color: keep hue, boost saturation, set lightness to ~52%.
  return hslToHex(hsl.h, Math.max(hsl.s, 0.55), 0.52);
}

// ── Default Palette (Tailwind stock indigo) ──────────────────────────

/** Tailwind's default indigo palette — used when the user hasn't picked a custom color. */
export const DEFAULT_PALETTE: PaletteMap = {
  '50': '238 242 255', // #EEF2FF
  '100': '224 231 255', // #E0E7FF
  '200': '199 210 254', // #C7D2FE
  '300': '165 180 252', // #A5B4FC
  '400': '129 140 248', // #818CF8
  '500': '99 102 241', // #6366F1
  '600': '79 70 229', // #4F46E5
  '700': '67 56 202', // #4338CA
  '800': '55 48 163', // #3730A3
  '900': '49 46 129', // #312E81
  '950': '30 27 75', // #1E1B4B
};

/** The default rail color — white (no theming applied). */
export const DEFAULT_RAIL_COLOR = '#FFFFFF';

// ── Blend ratios ─────────────────────────────────────────────────────

const LIGHT_BLENDS: { shade: PaletteShade; amount: number }[] = [
  { shade: '400', amount: 0.22 },
  { shade: '300', amount: 0.45 },
  { shade: '200', amount: 0.62 },
  { shade: '100', amount: 0.78 },
  { shade: '50', amount: 0.9 },
];

const DARK_BLENDS: { shade: PaletteShade; amount: number }[] = [
  { shade: '600', amount: 0.15 },
  { shade: '700', amount: 0.3 },
  { shade: '800', amount: 0.45 },
  { shade: '900', amount: 0.58 },
  { shade: '950', amount: 0.72 },
];

// ── Core derivation ──────────────────────────────────────────────────

/**
 * Derive a full 11-shade palette from a single `railColor`.
 *
 * - **White (#FFFFFF)**: returns the default Indigo palette (no theming).
 * - **Mid-tone / light color**: used directly as shade 500.
 * - **Dark color**: a vibrant shade 500 is derived via HSL manipulation —
 *   keeping the hue, boosting saturation, and raising lightness to ~52%.
 *
 * All 11 shades (50–950) are then generated from the shade 500 anchor by
 * blending toward white (lighter) or black (darker).
 */
export function derivePalette(railColor: string): PaletteMap {
  // Default / white rail → stock indigo palette (no re-theming).
  if (railColor.toUpperCase() === DEFAULT_RAIL_COLOR) {
    return DEFAULT_PALETTE;
  }

  // Determine the shade 500 anchor.
  const anchor = isDarkColor(railColor) ? deriveVibrantMidTone(railColor) : railColor;

  const palette: Partial<PaletteMap> = {
    '500': hexToTriplet(anchor),
  };

  // Lighter shades from 500.
  for (const { shade, amount } of LIGHT_BLENDS) {
    palette[shade] = hexToTriplet(blendHex(anchor, 'white', amount));
  }

  // Darker shades from 500.
  for (const { shade, amount } of DARK_BLENDS) {
    palette[shade] = hexToTriplet(blendHex(anchor, 'black', amount));
  }

  return palette as PaletteMap;
}
